/**
 * The way OUT of an honest failed state.
 *
 * `lib/honestState.ts` stopped six screens saying everything was fine when a
 * read had failed. That fix was only half the story, and an adversarial
 * review found the other half by execution: a Firestore `onSnapshot` error is
 * TERMINAL. The listener is dead, nothing resubscribes, and the honest "we
 * could not check" message sits there forever. The reviewer denied a read,
 * restored it with the page still open, waited 25 seconds, and the screen was
 * still showing the failure over live data. The only cure was a reload, which
 * is exactly what the copy ("pull down to refresh") was reduced to asking for.
 *
 * This module is the resubscribe. Hooks put `useReadEpoch()` in their effect
 * deps; bumping the epoch tears every wired listener down and builds it again
 * from scratch, which is the only thing that revives a dead one.
 *
 * TWO DESIGN DECISIONS WORTH KEEPING:
 *
 * 1. The epoch NEVER moves on its own. A healthy session bumps it zero times,
 *    so no user pays a resubscribe (or its reads) for a mechanism that exists
 *    for the broken case. `RetryReads` mounts only inside a failed state, and
 *    the automatic retries are attached there rather than app-wide.
 *
 * 2. A resubscribe is worthless unless the hook also clears its error on the
 *    next good snapshot. Without that the effect reruns, the data arrives, and
 *    the failed UI stays wedged over it because `error` is still set. Every
 *    hook wired to the epoch calls `setError(null)` in its success callback -
 *    check that first if a retry ever appears to do nothing.
 */
import { useSyncExternalStore } from 'react';

let epoch = 0;
/**
 * `null` means no retry has happened in this page's life, and it has to be
 * null rather than any number. `performance.now()` is milliseconds since the
 * page loaded, so a 0 here is not "never", it is a claim that a retry
 * happened at page load - which silently swallows every automatic retry in
 * the first three seconds of every page's life. That is the phone that
 * reconnects the moment the app opens, which is the ordinary case, and it
 * shipped that way until a test caught it.
 *
 * It was -Infinity for a while, which behaves correctly but is a magic value
 * a later edit can quietly "tidy" back to 0 with no test noticing, because
 * every test resets first and so never exercises what production boots with.
 * A reviewer proved exactly that mutant survives. `null` cannot be tidied
 * into a number without the type complaining, and the check below reads as
 * what it means.
 */
let lastRetryAt: number | null = null;
const subscribers = new Set<() => void>();

/** Automatic retries are throttled; a person pressing the button is not.
 *  'online' and 'visibilitychange' commonly fire together when a phone comes
 *  back from sleep, and one resubscribe answers both. */
const AUTO_RETRY_MIN_GAP_MS = 3000;

function bump(): void {
  epoch += 1;
  for (const notify of Array.from(subscribers)) notify();
}

/**
 * Resubscribe every wired listener. Call this from a person's explicit "Try
 * again", where an immediate visible response matters more than the cost of
 * one extra round trip.
 */
export function retryReads(now?: number): void {
  lastRetryAt = clockOr(now);
  bump();
}

/**
 * The same thing, for a signal rather than a person: the network came back,
 * or the tab did. Throttled, because those two arrive together constantly and
 * a phone waking on a flaky connection could otherwise resubscribe in a loop.
 */
export function retryReadsThrottled(now?: number): void {
  const at = clockOr(now);
  if (lastRetryAt !== null && at - lastRetryAt < AUTO_RETRY_MIN_GAP_MS) return;
  lastRetryAt = at;
  bump();
}

/**
 * The clock, with a seam for tests. Both retry functions take an optional
 * `now` so the three-second window can be pinned exactly rather than by
 * sleeping, which is the only way to test a boundary without making the
 * suite slow AND flaky.
 *
 * The typeof guard is not decoration. These are called from DOM event
 * handlers, and wiring one up directly - `addEventListener('online',
 * retryReadsThrottled)` instead of the arrow the component uses - would hand
 * an Event object in as `now`. Anything that is not a finite number is
 * ignored in favour of the real clock.
 */
function clockOr(now?: number): number {
  if (typeof now === 'number' && Number.isFinite(now)) return now;
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now();
}

/** The store half of useReadEpoch, exported so it can be pinned by a test
 *  without React. Returns its own unsubscribe. */
export function subscribeReadEpoch(callback: () => void): () => void {
  subscribers.add(callback);
  return () => {
    subscribers.delete(callback);
  };
}

/** Current value. Only meaningful as an effect dep; the number itself means
 *  nothing beyond "this changed, build the listener again". */
export function getReadEpoch(): number {
  return epoch;
}

/**
 * Put this in a data hook and pass the result into its effect deps. It is a
 * plain counter, so a hook that never sees a retry never re-runs because of
 * it.
 */
export function useReadEpoch(): number {
  return useSyncExternalStore(subscribeReadEpoch, getReadEpoch, getReadEpoch);
}

/** Test seam only. Not used by the app. */
export function resetReadEpochForTests(): void {
  epoch = 0;
  lastRetryAt = null;
  subscribers.clear();
}
